import React, { useEffect, useRef } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

const GrapesJSViewer = ({ grapesJsData, htmlContent, cssContent }) => {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const viewerRef = useRef(null);

  useEffect(() => {
    if (!grapesJsData && !htmlContent) {
      return;
    }

    // Clean up any existing editor
    if (editorRef.current) {
      try {
        editorRef.current.destroy();
      } catch (error) {
        // Silently handle editor destruction errors
      }
      editorRef.current = null;
    }

    // Clear container
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // First, try to use GrapesJS viewer mode
    try {
      // Initialize GrapesJS in viewer mode
      const editor = grapesjs.init({
        container: containerRef.current,
        height: 'auto',
        width: '100%',
        
        // Minimal viewer configuration
        panels: { defaults: [] },
        blockManager: { blocks: [] },
        layerManager: { appendTo: null },
        styleManager: { appendTo: null },
        traitManager: { appendTo: null },
        selectorManager: { appendTo: null },
        
        // Disable editing capabilities
        storageManager: false,
        noticeOnUnload: false,
        showOffsets: false,
        fromElement: false,
        
        // Canvas configuration for viewer mode
        canvas: {
          styles: [],
          scripts: [],
          customBadgeLabel: false,
          autoscroll: false
        },
        
        // Disable commands
        commands: { defaults: [] },
        
        // Disable default CSS to prevent conflicts
        avoidInlineStyle: false,
      });

      // Load the project data
      let projectLoaded = false;
      
      if (grapesJsData) {
        try {
          // Parse GrapesJS data if it's a string
          let projectData = typeof grapesJsData === 'string' 
            ? JSON.parse(grapesJsData) 
            : grapesJsData;
          
          // Fix CSS selectors - ensure class selectors have proper dot prefix
          if (projectData.styles) {
            projectData.styles.forEach(style => {
              if (style.selectors) {
                style.selectors = style.selectors.map(selector => {
                  // If selector looks like a class name without dot, add it
                  if (!selector.startsWith('.') && !selector.startsWith('#') && !selector.includes(' ')) {
                    return '.' + selector;
                  }
                  return selector;
                });
              }
            });
          }
          
          // Load the project
          editor.loadProjectData(projectData);
          projectLoaded = true;
          
        } catch (error) {
          // GrapesJS project data loading failed
          projectLoaded = false;
        }
      }
      
      // Fallback to HTML content if GrapesJS data failed to load
      if (!projectLoaded && htmlContent) {
        try {
          editor.setComponents(htmlContent);
          if (cssContent) {
            editor.setStyle(cssContent);
          }
          
          projectLoaded = true;
        } catch (error) {
          // HTML content loading failed
          projectLoaded = false;
        }
      }

      // If project loaded successfully, configure viewer mode
      if (projectLoaded) {
        // Configure viewer mode after load
        editor.on('load', () => {
          try {
            const canvas = editor.Canvas;
            const canvasEl = canvas.getElement();
            
            if (canvasEl) {
              // Make canvas read-only
              canvasEl.style.pointerEvents = 'auto';
              canvasEl.style.userSelect = 'auto';
              
              // Find the iframe and configure it
              const iframe = canvasEl.querySelector('iframe');
              if (iframe && iframe.contentDocument) {
                const iframeDoc = iframe.contentDocument;
                const iframeBody = iframeDoc.body;
                
                if (iframeBody) {
                  // Enable interactions for the content
                  iframeBody.style.pointerEvents = 'auto';
                  iframeBody.style.userSelect = 'auto';
                  
                  // Apply responsive styles
                  iframeBody.style.margin = '0';
                  iframeBody.style.padding = '0';
                  iframeBody.style.width = '100%';
                  iframeBody.style.boxSizing = 'border-box';
                }
              }
            }
            
            // Clear any selection
            editor.select();
            
          } catch (error) {
            // Silently handle viewer mode configuration errors
          }
        });

        // Store editor reference
        editorRef.current = editor;

        // Handle responsive behavior
        const handleResize = () => {
          try {
            if (editorRef.current) {
              const canvas = editorRef.current.Canvas;
              if (canvas && canvas.refresh) {
                canvas.refresh();
              }
            }
          } catch (error) {
            // Silently handle resize errors
          }
        };

        window.addEventListener('resize', handleResize);

        // Cleanup function
        return () => {
          window.removeEventListener('resize', handleResize);
          if (editorRef.current) {
            try {
              editorRef.current.destroy();
            } catch (error) {
              // Silently handle editor cleanup errors
            }
            editorRef.current = null;
          }
        };
      } else {
        // If project loading failed, fall back to simple HTML rendering
        throw new Error('Project loading failed');
      }

    } catch (error) {
      // Fallback to simple HTML rendering
      if (containerRef.current && (htmlContent || grapesJsData)) {
        const contentToRender = htmlContent || '';
        const stylesToApply = cssContent || '';
        
        // Create a simple content renderer
        const contentDiv = document.createElement('div');
        contentDiv.className = 'simple-html-renderer';
        contentDiv.style.cssText = `
          padding: 20px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          min-height: 300px;
          width: 100%;
        `;
        
        // Add content
        contentDiv.innerHTML = contentToRender;
        
        // Apply CSS styles
        if (stylesToApply) {
          const style = document.createElement('style');
          style.textContent = stylesToApply;
          contentDiv.appendChild(style);
        }
        
        containerRef.current.appendChild(contentDiv);
      }
    }
    
  }, [grapesJsData, htmlContent, cssContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        try {
          editorRef.current.destroy();
        } catch (error) {
          // Silently handle editor unmount errors
        }
        editorRef.current = null;
      }
    };
  }, []);

  return (
    <div className="grapesjs-viewer" ref={viewerRef}>
      <div 
        ref={containerRef}
        className="grapesjs-viewer-container"
        style={{
          width: '100%',
          minHeight: '400px',
          position: 'relative',
          background: '#fff'
        }}
      />
    </div>
  );
};

export default GrapesJSViewer;
